/**
 * Shared types and utilities for Drawer Facet components.
 * Extracted from WorkItemDrawer.tsx to avoid circular imports.
 */
import { WorkItem, WorkItemUpdate, WorkItemSafety } from '../api/workItems';
import type { DrawerStake, NextStep, NextStepCtaKind } from '../utils/deriveNextStep';
import {
    calendarYmdInZone,
    effectiveUserTimeZone,
    normalizeWallTimeInZone,
} from '../utils/userTime';

// ── Facet enum ──────────────────────────────────────────────────────────
export type Facet = 'triage' | 'work' | 'intel';

// ── Domain types ────────────────────────────────────────────────────────
export interface DomainMenuItem {
    key: string;
    label: string;
    icon: string;
    disabled?: boolean;
    statusLabel?: string;
}

// ── Extraction / Intelligence types ─────────────────────────────────────
export interface DecisionPathway {
    id: string;
    label: string;
    outcome: string;
    steps: string[];
    avoid: string;
}

export interface DecisionGuide {
    calm_summary: string;
    facts: { label: string; value: string; evidence: string }[];
    unknowns: { label: string; why: string }[];
    safe_first_step: string;
    pathways: DecisionPathway[];
    templates: { call_script?: string; letter_template?: string };
    disclaimer: string;
}

export interface ScamRisk {
    level: 'low' | 'medium' | 'high';
    reasons: string[];
    recommended_first_step?: string;
    confidence?: 'low' | 'medium' | 'high';
}

export interface InvoiceData {
    vendor?: string | null;
    invoice_number?: string | null;
    total_amount?: string | null;
    amount?: string | null;
    due_date?: string | null;
}

export interface ContactInfo {
    emails?: string[];
    phones?: string[];
    websites?: string[];
}

export interface EvidenceField<T = string | number | null> {
    value?: T | null;
    evidence?: string | null;
    confidence?: number | null;
    derived_from?: string | null;
}

export interface ExtractionFields {
    document_date?: EvidenceField<string>;
    due_date?: EvidenceField<string>;
    amount_due?: EvidenceField<number>;
    reference_id?: EvidenceField<string>;
}

export interface NoticeData {
    agency_or_sender?: string | null;
    notice_id?: string | null;
    case_id?: string | null;
    action_required?: string | null;
    response_deadline?: string | null;
    consequences_keywords?: string[];
    contact_channels?: ContactInfo;
}

// ── Shared props passed from WorkItemDrawer to all facets ───────────────
export interface DrawerFacetProps {
    item: WorkItem | null;
    draft: WorkItemUpdate;
    onFieldChange: (field: keyof WorkItemUpdate, value: any) => void;
    saving: boolean;
    showToast: (message: string, severity: 'success' | 'error') => void;
    onEmailExpandedChange?: (expanded: boolean) => void;
    emailViewerExpanded?: boolean;
    /** After Gmail returns 401 / auth errors from email endpoints, refresh bootstrap so header + Profile match. */
    onMailboxGmailAuthIssue?: () => void;
    /** Body checks update the canonical item's safety before another action is offered. */
    onEmailSafetyChange?: (safety: WorkItemSafety | null) => void;
}

// ── Triage facet props ──────────────────────────────────────────────────
export type TriageSnoozeDays = 1 | 3 | 7;

/**
 * Triage verbs rendered as a button row at the top of the Triage facet.
 * Every handler must be the same one wired to the ⋮ menu so both paths share
 * one mutation, one success toast, and one Undo.
 */
export interface TriageActions {
    onStart: () => void;
    onSnooze: (days: TriageSnoozeDays) => void;
    onDismiss: () => void;
    onClassifyApplication: () => void;
    onClassifyTask: () => void;
    /** Present only when the item is source-teach eligible. */
    onIgnoreSource?: () => void;
    currentType?: string | null;
    disabled: boolean;
}

export interface DrawerFacetTriageProps extends DrawerFacetProps {
    scamRisk: ScamRisk | null;
    invoiceData?: InvoiceData | null;
    extractionFields?: ExtractionFields | null;
    /** Provided by the drawer only while the item is in TRIAGE. */
    triageActions?: TriageActions;
    /** The source tile already shows this summary. Do not repeat it. */
    hideReadSummary?: boolean;
    nextStep?: NextStep | null;
    stakes?: DrawerStake[];
    onNextStepCta?: (kind: NextStepCtaKind, payload?: string) => void;
}

// ── Work facet props ────────────────────────────────────────────────────
export interface DrawerFacetWorkProps extends DrawerFacetProps {
    isTriageState: boolean;
    isJobDomain: boolean;
    /** Profile timezone, else the browser zone — Schedule dates use this day. */
    timeZone?: string | null;
    domainMenu: DomainMenuItem[];
    domainFieldEntries: { key: string; label: string; value: string }[];
    currentStateLabel: string;
    // Event / Prep
    showEventDate: boolean;
    setShowEventDate: (v: boolean) => void;
    suggestedActionDate: string | null;
    prepDaysUsed: number | null;
    detectedEventType: string | null;
    applySuggestion: () => void;
    startOnEventDay: () => void;
    nextStep?: NextStep | null;
    stakes?: DrawerStake[];
    onNextStepCta?: (kind: NextStepCtaKind, payload?: string) => void;
}

/** get_document-backed decision guide fetch lifecycle for the intel facet */
export type DrawerDecisionGuideFetchStatus = 'inactive' | 'loading' | 'success' | 'error';

// ── Intel facet props ───────────────────────────────────────────────────
export interface DrawerFacetIntelProps extends DrawerFacetProps {
    decisionGuideFetchStatus: DrawerDecisionGuideFetchStatus;
    onRetryDecisionGuide?: () => void;
    decisionGuide: DecisionGuide | null;
    scamRisk: ScamRisk | null;
    invoiceData: InvoiceData | null;
    contactInfo: ContactInfo | null;
    noticeData: NoticeData | null;
    extractionFields: ExtractionFields | null;
    reviewRequired: boolean;
    reviewReasons: string[];
    scamExpanded: boolean;
    onToggleScamDetails: () => void;
    onCopy: (value: string) => void;
}

// ── Utilities ───────────────────────────────────────────────────────────
export const toDateInputValue = (iso: string | null | undefined) => {
    if (!iso) return '';
    const localDatePrefix = iso.match(/^(\d{4}-\d{2}-\d{2})/);
    if (localDatePrefix) return localDatePrefix[1];
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
};

export const fromDateInputValue = (dateStr: string) => {
    if (!dateStr) return null;
    return `${dateStr}T00:00:00`;
};

/** Clock part of a stored due. Midnight is date-only, so the time field stays empty. */
export const toTimeInputValue = (iso: string | null | undefined) => {
    if (!iso) return '';
    const match = iso.match(/^\d{4}-\d{2}-\d{2}T(\d{2}):(\d{2})/);
    if (!match) return '';
    if (match[1] === '00' && match[2] === '00') return '';
    return `${match[1]}:${match[2]}`;
};

export const localYmd = (date = new Date(), timeZone?: string | null) => {
    return calendarYmdInZone(date, effectiveUserTimeZone(timeZone));
};

/** Compose due_date. Empty or 00:00 time stays a quiet date-only midnight.
 *  An empty date clears the due. fillMissingDate is only for choosing a time
 *  before a date — clearing an existing date must not snap back to today.
 *  Missing dates use the effective timezone's calendar day. DST-gap clocks
 *  move to the next real minute so the saved time matches the chip.
 */
export const fromDateTimeInputValue = (
    dateStr: string,
    timeStr: string,
    options?: { fillMissingDate?: boolean; timeZone?: string | null },
) => {
    const zone = effectiveUserTimeZone(options?.timeZone);
    const date = dateStr || (options?.fillMissingDate && timeStr ? localYmd(new Date(), zone) : '');
    if (!date) return null;
    const match = (timeStr || '').match(/^(\d{1,2}):(\d{2})/);
    if (!match) return `${date}T00:00:00`;
    const hour = Number(match[1]);
    const minute = Number(match[2]);
    if (!Number.isFinite(hour) || !Number.isFinite(minute) || (hour === 0 && minute === 0)) {
        return `${date}T00:00:00`;
    }
    const wall = normalizeWallTimeInZone(date, hour, minute, zone);
    return `${wall.ymd}T${String(wall.hour).padStart(2, '0')}:${String(wall.minute).padStart(2, '0')}:00`;
};

export const scheduleBadgeLabel = (iso: string | null | undefined) => {
    const date = toDateInputValue(iso);
    if (!date) return null;
    const time = toTimeInputValue(iso);
    return time ? `${date} ${time}` : date;
};

export const CRITICAL_CONFIDENCE_THRESHOLD = 0.85;

export const REVIEW_REASON_LABELS: Record<string, string> = {
    missing_due_date: 'Missing due date',
    missing_amount_due: 'Missing amount due',
    invalid_due_date: 'Invalid due date',
    invalid_document_date: 'Invalid document date',
    invalid_amount_due: 'Invalid amount due',
    invalid_reference_id: 'Invalid reference ID',
    relative_deadline_without_document_date: 'Relative deadline without document date',
    parse_failed: 'Parsing failed',
    api_unavailable: 'Extraction unavailable',
};
